import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, FileText } from "lucide-react";

const LOGO_WHITE = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";

const EDITIONS = {
  "2026": {
    year: "2026",
    tag: "Current Edition",
    title: "Text Racing",
    subtitle: "A Digital Sport for a New World",
    meta: "© 2026 · Mozo Marketing · Rev. 1",
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ni8gij3d_Text%20Racing%20-%20A%20Digital%20Sport%20For%20A%20New%20Worl%20REVISION%201.pdf",
    archived: false,
  },
  "2021": {
    year: "2021",
    tag: "Archived · Original",
    title: "Text Racing",
    subtitle: "A Digital Sport for a New World",
    meta: "© 2021 · Original Publication",
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/m3b8jak0_A%20DIGITAL%20SPORT%20FOR%20A%20NEW%20WORLD%20copy.pdf",
    archived: true,
  },
};

const INK = "#0b0b0c", PAPER = "#f5f4f0", LINE = "#2a2a2e", MUTED = "#9a9a96", MUTEDP = "#5c5b56", LINEP = "#d8d6cf";
const display = "font-['Big_Shoulders_Display'] font-black uppercase";
const mono = "font-['JetBrains_Mono']";
const body = "font-['Archivo']";

function Cover({ ed, active, onClick }) {
  const gradient = ed.archived
    ? "linear-gradient(160deg,#3a3a3d,#151517)"
    : "linear-gradient(160deg,#0f766e,#052e2b)";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`publication-card-${ed.year}`}
      aria-pressed={active}
      className="group text-left w-full transition-transform duration-200 hover:-translate-y-1 focus:outline-none"
    >
      <div className="border" style={{ borderColor: active ? PAPER : LINE, background: INK }}>
        {/* Cover art */}
        <div className="relative overflow-hidden" style={{ aspectRatio: "3 / 2" }}>
          <div className="absolute inset-0" style={{ background: gradient, filter: ed.archived ? "grayscale(1)" : "none" }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center" style={{ color: PAPER }}>
            <img src={LOGO_WHITE} alt="Text Racing" className="h-8 w-auto mb-3 opacity-95" style={{ filter: ed.archived ? "grayscale(1)" : "none" }} />
            <div className={`${display} text-xl sm:text-2xl leading-[0.95]`} style={{ textWrap: "balance" }}>{ed.subtitle}</div>
          </div>
          {/* Year badge */}
          <div className={`${mono} absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1`}
            style={{ background: ed.archived ? "#6b6b6b" : PAPER, color: ed.archived ? "#fff" : INK }}>
            {ed.year} Copyright
          </div>
        </div>
        {/* Card footer */}
        <div className="p-5" style={{ background: active ? PAPER : "transparent", color: active ? INK : PAPER, transition: "background 150ms,color 150ms" }}>
          <div className={`${mono} text-[10px] uppercase tracking-[0.2em] mb-1`} style={{ color: active ? MUTEDP : MUTED }}>{ed.tag}</div>
          <div className={`${display} text-2xl leading-none`}>{ed.title}</div>
          <div className={`${body} text-[12px] mt-2`} style={{ color: active ? MUTEDP : MUTED }}>{ed.meta}</div>
          <div className={`${mono} text-[11px] uppercase tracking-[0.15em] mt-4 inline-flex items-center gap-2`}>
            {active ? "Now viewing ↓" : "Click to view →"}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function Publication() {
  const [selected, setSelected] = useState("2026");
  const ed = EDITIONS[selected];

  return (
    <div className={`${body} min-h-screen flex flex-col`} style={{ background: INK, color: PAPER }} data-testid="publication-page">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b" style={{ background: "rgba(11,11,12,0.82)", backdropFilter: "blur(12px)", borderColor: LINE }}>
        <div className="mx-auto flex items-center justify-between gap-4 px-6 md:px-8 h-16" style={{ maxWidth: 1120 }}>
          <Link to="/" data-testid="publication-back" className={`${mono} text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-2 hover:opacity-80`} style={{ color: MUTED }}>
            <ArrowLeft size={16} /> Home
          </Link>
          <img src={LOGO_WHITE} alt="Text Racing" className="h-7 w-auto" />
          <span className="w-[52px]" />
        </div>
      </header>

      {/* Title */}
      <section className="px-6 md:px-8 pt-12 pb-6">
        <div className="mx-auto" style={{ maxWidth: 1120 }}>
          <div className={`${mono} text-[11px] uppercase tracking-[0.18em] flex items-center gap-2 mb-5`} style={{ color: MUTED }}>
            <span className="inline-block h-px w-[18px]" style={{ background: "currentColor" }} />
            Publications
          </div>
          <h1 className={`${display} leading-[0.88] text-5xl sm:text-6xl lg:text-7xl mb-5`} style={{ textWrap: "balance", transform: "skewX(-4deg)", transformOrigin: "left" }}>
            Digital Sport for a New World
          </h1>
          <p className={`${body} text-base sm:text-lg font-semibold max-w-2xl`} style={{ color: PAPER }}>
            Two editions chart the evolution of Text Racing and "Texting as a Sport" - the archived <span style={{ color: MUTED }}>2021</span> original and the current <span style={{ color: MUTED }}>2026</span> revision. Pick an edition to read it.
          </p>
        </div>
      </section>

      {/* Two editions side by side - 2021 (left, archived) · 2026 (right, current) */}
      <section className="px-6 md:px-8 pb-4">
        <div className="mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6" style={{ maxWidth: 1120 }} data-testid="publication-editions">
          <Cover ed={EDITIONS["2021"]} active={selected === "2021"} onClick={() => setSelected("2021")} />
          <Cover ed={EDITIONS["2026"]} active={selected === "2026"} onClick={() => setSelected("2026")} />
        </div>
      </section>

      {/* Selected edition actions */}
      <section className="px-6 md:px-8 pt-6">
        <div className="mx-auto flex flex-wrap items-center justify-between gap-4" style={{ maxWidth: 1120 }}>
          <div>
            <div className={`${mono} text-[11px] uppercase tracking-[0.2em]`} style={{ color: MUTED }}>Now viewing</div>
            <div className={`${display} text-3xl`}>{ed.year} Copyright{ed.archived ? " · Archived" : " · Current"}</div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href={ed.url} download data-testid="publication-download"
              className={`${body} font-semibold inline-flex items-center justify-center gap-2 px-6 py-3 text-sm tracking-wide`} style={{ background: PAPER, color: INK }}>
              <Download size={16} /> Download
            </a>
            <a href={ed.url} target="_blank" rel="noopener noreferrer" data-testid="publication-open-tab"
              className={`${body} font-semibold inline-flex items-center justify-center gap-2 px-6 py-3 text-sm tracking-wide border`} style={{ borderColor: PAPER, color: PAPER }}>
              <ExternalLink size={16} /> Open in new tab
            </a>
          </div>
        </div>
      </section>

      {/* Embedded viewer for the selected edition */}
      <section className="px-6 md:px-8 py-6 flex-1">
        <div className="mx-auto" style={{ maxWidth: 1120 }}>
          <div className="border overflow-hidden" style={{ borderColor: LINE, background: PAPER, filter: ed.archived ? "grayscale(1)" : "none" }}>
            <object key={selected} data={ed.url} type="application/pdf" className="w-full" style={{ height: "80vh", minHeight: 520 }} data-testid="publication-viewer">
              <div className="flex flex-col items-center justify-center gap-4 text-center p-12" style={{ color: INK }}>
                <FileText size={40} />
                <p className={`${body} text-[15px] max-w-sm`}>Your browser can't display the PDF inline. Use the buttons above to open or download it.</p>
                <a href={ed.url} target="_blank" rel="noopener noreferrer"
                  className={`${body} font-semibold inline-flex items-center gap-2 px-6 py-3 text-sm`} style={{ background: INK, color: PAPER }}>
                  <ExternalLink size={16} /> Open the Publication
                </a>
              </div>
            </object>
          </div>
        </div>
      </section>
    </div>
  );
}
